import React, { useEffect, useRef } from "react"
import { CARDS_FLIPPED_EVENT } from "../util/util"

const flakeSource = require("../assets/backgrounds/snowflake.png") as string

interface Flake {
    x: number
    y: number
    vx: number
    vy: number
    rotation: number
    spin: number
    sway: number
    swaySpeed: number
    swayAmp: number
    size: number
    depth: number
    fallMul: number
}

interface WindRibbon {
    originX: number
    originY: number
    dir: number
    speed: number
    t: number
    duration: number
    waveK: number
    waveK2: number
    amp: number
    amp2: number
    loopAt: number
    loopDur: number
    loopRadius: number
    loopCenterX: number
    loopCenterY: number
    startA: number
    fadeWindow: number
    trail: { x: number; y: number }[]
}

function rand(min: number, max: number) {
    return min + Math.random() * (max - min)
}

function loadFlake(): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = flakeSource
    })
}

export default function FallingSnowBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) {
            return
        }
        const ctx = canvas.getContext("2d", { alpha: true, desynchronized: true })
        if (!ctx) {
            return
        }

        let width = 0
        let height = 0
        let raf = 0
        let running = true
        let last = performance.now()
        let wind = 5
        let targetWind = 7
        let gustTimer = 2.4
        let pendingSpawns = 0
        let spawnWait = 0.8
        let burstSpawns = false
        let burstLanes: number[] = []
        let sortTick = 0
        const flakes: Flake[] = []
        const ribbons: WindRibbon[] = []
        let flakeImg: HTMLImageElement | null = null

        function resize() {
            width = window.innerWidth
            height = window.innerHeight
            const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
            const cap = Math.min(1, 1920 / Math.max(1, width), 1080 / Math.max(1, height))
            const scale = dpr * cap
            canvas!.width = Math.max(1, Math.floor(width * scale))
            canvas!.height = Math.max(1, Math.floor(height * scale))
            canvas!.style.width = `${width}px`
            canvas!.style.height = `${height}px`
            ctx!.setTransform(scale, 0, 0, scale, 0, 0)
            ctx!.imageSmoothingEnabled = cap < 0.999
        }

        function spawnFlake(anywhere: boolean): Flake {
            const depth = Math.pow(Math.random(), 0.7)
            return {
                x: rand(-30, width + 30),
                y: anywhere ? rand(-40, height) : rand(-90, -12),
                vx: rand(-2.2, 2.2),
                vy: rand(3.5, 20),
                rotation: rand(0, Math.PI * 2),
                spin: rand(-0.28, 0.28),
                sway: rand(0, Math.PI * 2),
                swaySpeed: rand(0.28, 1.35),
                swayAmp: rand(6, 26),
                size: (7 + depth * 18) * 1.5,
                depth,
                fallMul: rand(0.5, 1.65),
            }
        }

        function sinePoint(r: Pick<WindRibbon, "originX" | "originY" | "dir" | "speed" | "waveK" | "waveK2" | "amp" | "amp2">, pathT: number) {
            const s = pathT * r.speed
            return {
                x: r.originX + r.dir * s,
                y: r.originY + Math.sin(pathT * r.waveK) * r.amp + Math.sin(pathT * r.waveK2 + 0.6) * r.amp2,
            }
        }

        function spawnRibbon() {
            const dir = targetWind >= 0 ? 1 : -1
            const originX = dir > 0 ? rand(-80, width * 0.12) : rand(width * 0.88, width + 80)
            let originY: number
            if (burstLanes.length > 0) {
                originY = burstLanes.shift() as number
            } else {
                originY = rand(height * 0.2, height * 0.75)
            }
            const speed = rand(70, 115)
            const totalLife = 4.4
            const duration = rand(2.6, 3.0)
            const waveK = rand(0.9, 1.6)
            const waveK2 = waveK * 0.38
            const amp = rand(12, 26)
            const amp2 = amp * 0.24
            const hasLoop = Math.random() < 0.22
            const loopAt = hasLoop ? rand(duration * 0.24, duration * 0.48) : -1
            const loopRadius = rand(18, 32)
            const proto = { originX, originY, dir, speed, waveK, waveK2, amp, amp2 }
            const entry = loopAt >= 0 ? sinePoint(proto, loopAt) : { x: 0, y: 0 }
            const vx = dir * speed
            const vy = loopAt >= 0
                ? Math.cos(loopAt * waveK) * waveK * amp + Math.cos(loopAt * waveK2 + 0.6) * waveK2 * amp2
                : 0
            const mag = Math.hypot(vx, vy) || 1
            const tx = vx / mag
            const ty = vy / mag
            const startA = dir > 0 ? Math.atan2(tx, -ty) : Math.atan2(-tx, ty)
            const loopDur = loopAt >= 0 ? (Math.PI * 2 * loopRadius) / mag : 0
            ribbons.push({
                originX,
                originY,
                dir,
                speed,
                t: 0,
                duration,
                waveK,
                waveK2,
                amp,
                amp2,
                loopAt,
                loopDur,
                loopRadius,
                loopCenterX: entry.x - Math.cos(startA) * loopRadius,
                loopCenterY: entry.y - Math.sin(startA) * loopRadius,
                startA,
                fadeWindow: totalLife - duration,
                trail: [],
            })
        }

        function circlePoint(r: WindRibbon, u: number) {
            const a = r.startA - r.dir * u * Math.PI * 2
            return {
                x: r.loopCenterX + Math.cos(a) * r.loopRadius,
                y: r.loopCenterY + Math.sin(a) * r.loopRadius,
            }
        }

        function ribbonPoint(r: WindRibbon, t: number) {
            if (r.loopAt < 0) {
                return sinePoint(r, t)
            }
            const loopEnd = r.loopAt + r.loopDur
            if (t < r.loopAt) {
                return sinePoint(r, t)
            }
            if (t > loopEnd) {
                return sinePoint(r, t - r.loopDur)
            }
            return circlePoint(r, (t - r.loopAt) / r.loopDur)
        }

        function tick(now: number) {
            if (!running) {
                return
            }
            const dt = Math.min(0.033, (now - last) / 1000)
            last = now

            gustTimer -= dt
            if (gustTimer <= 0) {
                targetWind = rand(-18, 22)
                gustTimer = rand(3.6, 6.8)
                if (Math.random() < 0.7) {
                    pendingSpawns += 1
                }
            }
            spawnWait -= dt
            if (pendingSpawns > 0 && spawnWait <= 0) {
                spawnRibbon()
                pendingSpawns -= 1
                spawnWait = burstSpawns ? rand(0.85, 1.4) : rand(2.2, 3.6)
                if (pendingSpawns === 0) {
                    burstSpawns = false
                }
            }
            wind += (targetWind - wind) * (1 - Math.exp(-dt * 1.1))
            const breeze = Math.sin(now / 3800) * 2.4 + Math.sin(now / 6400) * 1.1
            const flow = wind * 0.4 + targetWind * 0.45 + breeze

            sortTick += 1
            if (sortTick % 12 === 0) {
                flakes.sort((a, b) => a.depth - b.depth)
            }
            for (const flake of flakes) {
                const parallax = 0.28 + flake.depth * 0.72
                flake.sway += flake.swaySpeed * dt
                const drift = Math.sin(flake.sway) * flake.swayAmp * 0.045
                const windForce = flow * (0.32 + flake.depth * 0.48)
                flake.vx += (windForce + drift - flake.vx) * dt * 1.25
                const fall = (5.2 + flake.size * 0.16) * parallax * flake.fallMul
                flake.vy += (fall - flake.vy) * dt * 0.7
                flake.vy = Math.min(Math.max(flake.vy, 2.4 * flake.fallMul), 24 * parallax * flake.fallMul)
                flake.x += flake.vx * dt * 2.8 * parallax
                flake.y += flake.vy * dt * 2.35 * parallax
                flake.rotation += flake.spin * dt

                if (flake.x < -40) {
                    flake.x = width + 30
                } else if (flake.x > width + 40) {
                    flake.x = -30
                }
                if (flake.y > height + 40) {
                    Object.assign(flake, spawnFlake(false))
                }
            }

            for (let i = ribbons.length - 1; i >= 0; i--) {
                const r = ribbons[i]
                const prevT = r.t
                r.t += dt
                const inLoop = r.loopAt >= 0 && r.t > r.loopAt && prevT < r.loopAt + r.loopDur
                const steps = inLoop ? Math.max(1, Math.ceil(dt / 0.008)) : 1
                for (let s = 1; s <= steps; s++) {
                    r.trail.push(ribbonPoint(r, prevT + (r.t - prevT) * (s / steps)))
                }
                if (r.trail.length > 360) {
                    r.trail.splice(0, r.trail.length - 360)
                }
                if (r.t > r.duration + r.fadeWindow) {
                    ribbons.splice(i, 1)
                }
            }

            for (const flake of flakes) {
                const parallax = 0.28 + flake.depth * 0.72
                for (const r of ribbons) {
                    if (r.trail.length < 2) {
                        continue
                    }
                    const head = r.trail[r.trail.length - 1]
                    const prev = r.trail[r.trail.length - 2]
                    const dist = Math.hypot(flake.x - head.x, flake.y - head.y)
                    const radius = 90 + flake.size
                    if (dist > radius) {
                        continue
                    }
                    const influence = (1 - dist / radius) * (0.22 + flake.depth * 0.35)
                    const rvx = (head.x - prev.x) / Math.max(dt, 0.001)
                    const rvy = (head.y - prev.y) / Math.max(dt, 0.001)
                    flake.x += rvx * dt * influence * 0.7
                    flake.y += rvy * dt * influence * 0.28
                    flake.vx += (rvx / (2.8 * parallax) * 0.55 - flake.vx) * dt * influence * 1.2
                }
            }

            ctx!.clearRect(0, 0, width, height)

            if (flakeImg) {
                for (const flake of flakes) {
                    const w = flake.size
                    ctx!.globalAlpha = 0.22 + flake.depth * 0.62
                    if (w < 12) {
                        ctx!.drawImage(flakeImg, flake.x - w / 2, flake.y - w / 2, w, w)
                    } else {
                        ctx!.save()
                        ctx!.translate(flake.x, flake.y)
                        ctx!.rotate(flake.rotation)
                        ctx!.drawImage(flakeImg, -w / 2, -w / 2, w, w)
                        ctx!.restore()
                    }
                }
            }

            ctx!.lineCap = "round"
            ctx!.lineJoin = "round"
            ctx!.strokeStyle = "#eaf4ff"
            for (const r of ribbons) {
                if (r.trail.length < 2) {
                    continue
                }
                const lifeFade = r.t <= r.duration
                    ? 1
                    : Math.max(0, 1 - (r.t - r.duration) / r.fadeWindow)
                if (lifeFade <= 0.01) {
                    continue
                }
                const n = r.trail.length - 1
                const step = Math.max(1, Math.floor(n / 64))
                ctx!.globalAlpha = 0.16 * lifeFade
                for (let i = step; i <= n; i += step) {
                    const prev = r.trail[i - step]
                    const cur = r.trail[Math.min(i, n)]
                    const u = i / n
                    ctx!.beginPath()
                    ctx!.lineWidth = 0.4 + Math.sin(u * Math.PI) * 3.4
                    ctx!.moveTo(prev.x, prev.y)
                    ctx!.lineTo(cur.x, cur.y)
                    ctx!.stroke()
                }
            }
            ctx!.globalAlpha = 1

            raf = requestAnimationFrame(tick)
        }

        function triggerCardFlipBurst(e: Event) {
            if ((e as CustomEvent).detail?.showingFront === false) {
                return
            }
            const dir = Math.random() < 0.5 ? -1 : 1
            targetWind = dir * rand(78, 118)
            wind = targetWind
            burstSpawns = true
            pendingSpawns = 3
            spawnWait = rand(0.35, 0.55)
            gustTimer = rand(4.2, 5.8)
            const lanes = [0.2, 0.5, 0.8]
            for (let i = lanes.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1))
                const tmp = lanes[i]
                lanes[i] = lanes[j]
                lanes[j] = tmp
            }
            burstLanes = lanes.map(u => height * (u + rand(-0.05, 0.05)))
            for (const flake of flakes) {
                const kick = rand(22, 48) * (0.45 + flake.depth * 0.8)
                flake.vx += dir * kick
                flake.spin += dir * rand(0.15, 0.45)
            }
        }

        resize()
        window.addEventListener("resize", resize)
        window.addEventListener(CARDS_FLIPPED_EVENT, triggerCardFlipBurst)

        loadFlake().then(loaded => {
            if (!running) {
                return
            }
            flakeImg = loaded
            const count = Math.round(Math.min(48, Math.max(28, Math.min(width, 1920) / 36)))
            for (let i = 0; i < count; i++) {
                flakes.push(spawnFlake(true))
            }
            last = performance.now()
            raf = requestAnimationFrame(tick)
        }).catch(() => undefined)

        return () => {
            running = false
            cancelAnimationFrame(raf)
            window.removeEventListener("resize", resize)
            window.removeEventListener(CARDS_FLIPPED_EVENT, triggerCardFlipBurst)
        }
    }, [])

    return (
        <canvas
            ref={canvasRef}
            className="falling-leaves-canvas"
            aria-hidden
        />
    )
}
